/**
 * useInspectionUploadQueue - Envolve a fila pura `InspectionUploadQueue` para uso em React.
 *
 * - Mantém as tarefas no estado para a tela mostrar progresso, "Enviando"/"Falhou" e o total.
 * - Guarda cada arquivo no aparelho (IndexedDB) até ser salvo na vistoria; ao reabrir o link,
 *   `restoreFromDevice` retoma o envio (só depois de a sessão e os itens estarem carregados).
 * - Reenvia automaticamente as falhas quando a internet volta (evento `online`).
 * - Pede confirmação ao fechar/sair da página enquanto houver envio pendente.
 * - Libera as miniaturas locais (object URLs) ao desmontar.
 *
 * RELEASE: 2026-10-14 v1.1.0 - Persistência no aparelho, retomada e progresso por arquivo.
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial (vistoria externa em segundo plano).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InspectionUploadQueue, type QueueDeps, type UploadTask } from '@/lib/inspectionUploadQueue';
import { deleteUpload, listUploads, markUploaded, putUpload, uploadStoreAvailable } from '@/lib/inspectionUploadStore';

type Deps<A> = Omit<QueueDeps<A>, 'onChange' | 'onAdded' | 'onUploaded' | 'onDone'> & {
  /** Token do link: separa os arquivos guardados de cada vistoria. */
  linkToken: string;
};

export function useInspectionUploadQueue<A>(deps: Deps<A>) {
  const [tasks, setTasks] = useState<UploadTask<A>[]>([]);
  const [storeAvailable, setStoreAvailable] = useState(true);
  // Dependências sempre atualizadas sem recriar a fila (as funções mudam a cada render).
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const queue = useMemo(() => new InspectionUploadQueue<A>({
    upload: (t, p) => depsRef.current.upload(t, p),
    save: (id, a) => depsRef.current.save(id, a),
    concurrency: deps.concurrency,
    maxAttempts: deps.maxAttempts,
    onChange: setTasks,
    // Persistência: guarda antes de enviar, marca enviado e apaga ao salvar.
    onAdded: (t) => {
      void putUpload<A>({
        id: t.id, linkToken: depsRef.current.linkToken, answerId: t.answerId, blob: t.file, name: t.file.name,
        type: t.file.type, fromCamera: t.fromCamera, uploaded: null, createdAt: Date.now(),
      }).then((ok) => { if (!ok) setStoreAvailable(false); });
    },
    onUploaded: (t) => { if (t.uploaded) void markUploaded(t.id, t.uploaded); },
    onDone: (t) => { void deleteUpload(t.id); },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void uploadStoreAvailable().then(setStoreAvailable); }, []);

  /**
   * Retoma arquivos guardados no aparelho. `isEditable` decide se o item ainda aceita envio;
   * os que não aceitam são descartados. Devolve quantos foram retomados e descartados.
   */
  const restoreFromDevice = useCallback(async (isEditable: (answerId: string) => boolean) => {
    const saved = await listUploads<A>(depsRef.current.linkToken);
    const keep = saved.filter((e) => isEditable(e.answerId));
    const drop = saved.filter((e) => !isEditable(e.answerId));
    await Promise.all(drop.map((e) => deleteUpload(e.id)));
    queue.restore(keep.map((e) => {
      const file = new File([e.blob], e.name, { type: e.type, lastModified: e.createdAt });
      return {
        id: e.id, answerId: e.answerId, file, fromCamera: e.fromCamera, uploaded: e.uploaded,
        previewUrl: e.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      };
    }));
    return { restored: keep.length, discarded: drop.length };
  }, [queue]);

  const hasPending = tasks.some((t) => t.status !== 'done');

  // Internet voltou: tenta novamente o que falhou.
  useEffect(() => {
    const onOnline = () => queue.retryAll();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [queue]);

  // Confirmação do navegador ao sair com arquivos ainda não salvos.
  useEffect(() => {
    if (!hasPending) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasPending]);

  // Libera miniaturas locais ao sair da tela.
  useEffect(() => () => { queue.list.forEach((t) => t.previewUrl && URL.revokeObjectURL(t.previewUrl)); }, [queue]);

  return { queue, tasks, hasPending, storeAvailable, restoreFromDevice };
}
